import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Mail, User, Briefcase, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("fis_admin_token") || ""
  );
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ leads: 0, applications: 0, chat_messages: 0 });
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("fis_admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Signed in");
    } catch (err) {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    localStorage.removeItem("fis_admin_token");
    setToken("");
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/leads`, { headers }),
      axios.get(`${API}/admin/applications`, { headers }),
      axios.get(`${API}/admin/stats`, { headers }),
    ])
      .then(([l, a, s]) => {
        setLeads(l.data);
        setApps(a.data);
        setStats(s.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) logout();
        else toast.error("Failed to load data");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (!token) {
    return (
      <main
        data-testid="admin-login-page"
        className="min-h-screen grid place-items-center bg-[color:var(--fis-bg-alt)] px-6"
      >
        <form
          onSubmit={login}
          className="bg-white border fis-hairline p-10 w-full max-w-md"
        >
          <Link to="/" className="font-mono-label">← Back to site</Link>
          <h1 className="font-display text-4xl mt-6">Admin access</h1>
          <p className="mt-3 text-[color:var(--fis-ink-soft)] text-sm">
            Enter the team password to manage leads and applications.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="fis-input mt-8"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="fis-btn fis-btn-primary mt-6 w-full justify-center"
          >
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[color:var(--fis-bg-alt)]"
    >
      <header className="bg-white border-b fis-hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-xl">FIS · Admin</Link>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="fis-btn fis-btn-outline"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StatCard icon={User} label="Leads" value={stats.leads} />
          <StatCard icon={Briefcase} label="Applications" value={stats.applications} />
          <StatCard icon={MessageSquare} label="Chat messages" value={stats.chat_messages} />
        </div>

        <div className="flex gap-2 mb-6">
          {["leads", "applications"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-4 py-2 text-sm border transition ${
                tab === t
                  ? "bg-[color:var(--fis-ink)] text-white border-transparent"
                  : "bg-white border-black/15 hover:border-[color:var(--fis-ink)]"
              }`}
            >
              {t === "leads" ? "Leads" : "Job Applications"}
            </button>
          ))}
        </div>

        {loading && <div className="text-[color:var(--fis-ink-soft)]">Loading…</div>}

        {tab === "leads" && !loading && (
          <div className="bg-white border fis-hairline overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--fis-bg-alt)] text-left">
                <tr>
                  {["Name", "Email", "Company", "Service", "Message", "Date"].map((h) => (
                    <th key={h} className="font-mono-label px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-[color:var(--fis-ink-soft)]">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-t fis-hairline">
                    <td className="px-4 py-3 font-medium">{l.name}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-[color:var(--fis-accent)]">
                        <Mail className="w-3 h-3" />{l.email}
                      </a>
                    </td>
                    <td className="px-4 py-3">{l.company || "—"}</td>
                    <td className="px-4 py-3">{l.service || "—"}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{l.message}</td>
                    <td className="px-4 py-3 text-[color:var(--fis-ink-soft)]">{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "applications" && !loading && (
          <div className="bg-white border fis-hairline overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--fis-bg-alt)] text-left">
                <tr>
                  {["Name", "Email", "Position", "Exp", "Portfolio", "Date"].map((h) => (
                    <th key={h} className="font-mono-label px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-[color:var(--fis-ink-soft)]">No applications yet.</td></tr>
                )}
                {apps.map((a) => (
                  <tr key={a.id} data-testid={`admin-app-row-${a.id}`} className="border-t fis-hairline">
                    <td className="px-4 py-3 font-medium">{a.name}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${a.email}`} className="text-[color:var(--fis-accent)]">{a.email}</a>
                    </td>
                    <td className="px-4 py-3">{a.position}</td>
                    <td className="px-4 py-3">{a.experience_years ?? "—"}</td>
                    <td className="px-4 py-3">
                      {a.portfolio_url ? (
                        <a href={a.portfolio_url} target="_blank" rel="noreferrer" className="text-[color:var(--fis-accent)]">link</a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-[color:var(--fis-ink-soft)]">{formatDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border fis-hairline p-6 flex items-center justify-between">
      <div>
        <div className="font-mono-label">{label}</div>
        <div className="font-display text-4xl mt-2">{value}</div>
      </div>
      <div className="w-12 h-12 bg-[color:var(--fis-bg-alt)] grid place-items-center">
        <Icon className="w-5 h-5 text-[color:var(--fis-accent)]" />
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
